import React from 'react';
import { Box, Chip, Divider, Grid, Typography } from '@mui/material';
import {
  Person as PersonIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DetailModal } from '../../ui/Modal';
import { User } from '../../../types/user.types';
import StatusChip from '../../ui/StatusChip/StatusChip';
import { colors } from '../../../styles/theme/colors';
import { UserDetailModalStyles } from './UserDetailModal.styles';

export interface UserDetailModalProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onEditUser?: (id: string) => void;
  className?: string;
  getPrimaryLocationName?: (primaryLocationId?: number | null) => string;
}

const UserDetailModalComponent: React.FC<UserDetailModalProps> = ({
  open,
  user,
  onClose,
  onEditUser,
  className,
  getPrimaryLocationName,
}) => {
  if (!user) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const fullName = `${user.firstName} ${user.lastName}`.trim();
  const primaryLocationName = getPrimaryLocationName?.(user.primaryLocationId) ?? '—';

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title="Detalle del Usuario"
      maxWidth="md"
      className={className}
      icon={<PersonIcon fontSize="small" />}
      footerActions={
        onEditUser ? (
          <UserDetailModalStyles.EditButton
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEditUser(user.id)}
          >
            Editar Usuario
          </UserDetailModalStyles.EditButton>
        ) : null
      }
    >
      <UserDetailModalStyles.Content>
        <UserDetailModalStyles.Section>
          <UserDetailModalStyles.SectionHeader>
            <BadgeIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Información Básica
            </Typography>
          </UserDetailModalStyles.SectionHeader>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Cuenta
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {user.email}
                </Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Nombre
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {fullName || '—'}
                </Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Teléfono
                </Typography>
                <Typography variant="body1">{user.phone || '—'}</Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Sucursal Asignada
                </Typography>
                <Typography variant="body1">{primaryLocationName}</Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Estado
                </Typography>
                <StatusChip
                  label={user.isActive ? 'Activo' : 'Bloqueado'}
                  status={user.isActive ? 'success' : 'error'}
                  size="small"
                  visualStyle="quiet"
                  withIcon={false}
                />
              </UserDetailModalStyles.InfoRow>
            </Grid>
          </Grid>
        </UserDetailModalStyles.Section>

        <UserDetailModalStyles.Section>
          <UserDetailModalStyles.SectionHeader>
            <PersonIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Roles asignados
            </Typography>
          </UserDetailModalStyles.SectionHeader>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {user.roles?.length ? (
              user.roles.map((role) => (
                <Chip
                  key={role.id}
                  label={role.name}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(34, 211, 238, 0.12)',
                    color: '#0891b2',
                    fontWeight: 600,
                  }}
                />
              ))
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Sin roles asignados
              </Typography>
            )}
          </Box>
        </UserDetailModalStyles.Section>

        <Divider sx={{ my: 2 }} />

        {/* Información de Bloqueo - Solo si está bloqueado */}
        {!user.isActive && (user.blockedAt || user.blockedBy || user.blockReason) && (
          <>
            <UserDetailModalStyles.Section>
              <UserDetailModalStyles.SectionHeader>
                <BlockIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                  Información de Bloqueo
                </Typography>
              </UserDetailModalStyles.SectionHeader>

              <Grid container spacing={3} sx={{ mt: 1 }}>
                {user.blockedAt && (
                  <Grid item xs={12} md={6}>
                    <UserDetailModalStyles.InfoRow>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.text.secondary, fontWeight: 500 }}
                      >
                        Fecha de bloqueo
                      </Typography>
                      <Typography variant="body2">{formatDate(user.blockedAt)}</Typography>
                    </UserDetailModalStyles.InfoRow>
                  </Grid>
                )}
                {user.blockedBy && (
                  <Grid item xs={12} md={6}>
                    <UserDetailModalStyles.InfoRow>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.text.secondary, fontWeight: 500 }}
                      >
                        Bloqueado por
                      </Typography>
                      <Typography variant="body2">{user.blockedBy}</Typography>
                    </UserDetailModalStyles.InfoRow>
                  </Grid>
                )}
                {user.blockReason && (
                  <Grid item xs={12}>
                    <UserDetailModalStyles.InfoRow>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.text.secondary, fontWeight: 500 }}
                      >
                        Razón del bloqueo
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {user.blockReason}
                      </Typography>
                    </UserDetailModalStyles.InfoRow>
                  </Grid>
                )}
              </Grid>
            </UserDetailModalStyles.Section>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <UserDetailModalStyles.Section>
          <UserDetailModalStyles.SectionHeader>
            <CalendarIcon fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Fechas y Auditoría
            </Typography>
          </UserDetailModalStyles.SectionHeader>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Creación
                </Typography>
                <Typography variant="body2">{formatDate(user.createdAt)}</Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            <Grid item xs={12} md={6}>
              <UserDetailModalStyles.InfoRow>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontWeight: 500 }}
                >
                  Última modificación
                </Typography>
                <Typography variant="body2">{formatDate(user.updatedAt)}</Typography>
              </UserDetailModalStyles.InfoRow>
            </Grid>
            {user.lastModifiedBy && (
              <Grid item xs={12} md={6}>
                <UserDetailModalStyles.InfoRow>
                  <Typography
                    variant="caption"
                    sx={{ color: colors.text.secondary, fontWeight: 500 }}
                  >
                    Modificado por
                  </Typography>
                  <Typography variant="body2">{user.lastModifiedBy}</Typography>
                </UserDetailModalStyles.InfoRow>
              </Grid>
            )}
          </Grid>
        </UserDetailModalStyles.Section>
      </UserDetailModalStyles.Content>
    </DetailModal>
  );
};

export default UserDetailModalComponent;
